// services/flashcardScheduling.ts
//
// The client-side FSRS step: given a card and the button the student pressed,
// what its next scheduling is. Extracted from `useFlashcardResponse` (ORA-680)
// so the in-chat study-set deck rates a card through exactly the same path as
// the full study surface — two implementations of an interval would mean a card
// rated in chat coming back at a different time than the same card rated in
// study, which the student would read as lost work.

import { FSRS, Rating } from './scheduler';
import type { Flashcard, FlashcardScheduling } from '../types/Flashcard';
import { getFlashcardVariants } from '../utils/flashcardVariants';

/** The card fields the scheduling step reads. */
export type SchedulableFlashcard = Pick<Flashcard, 'scheduling' | 'variants'>;

/** UI response (0–3) → FSRS rating (1–4). */
const RATING_BY_RESPONSE: { [key: number]: Rating } = {
  0: Rating.Again,
  1: Rating.Hard,
  2: Rating.Good,
  3: Rating.Easy,
};

/**
 * Which variant of the card to present next: a missed card steps back toward
 * the easiest wording, a remembered one steps forward, and neither may leave
 * the variants that exist.
 */
export const calcNewDifficultyIndex = (
  currentIndex: number,
  variantCount: number,
  response: number
): number =>
  response === 0 || response === 1
    ? Math.max(currentIndex - 1, 0)
    : Math.min(currentIndex + 1, variantCount - 1);

export interface NewSchedulingOptions {
  /**
   * How many variants the card has, when the caller knows something the card
   * object does not. The chat deck is served one presented variant rather than
   * the whole list, so it passes the count it can vouch for and the index never
   * advances past a variant that may not exist.
   */
  variantCount?: number;
  /**
   * The probability the student wants to recall a card when it is due. The
   * scheduler clamps invalid or out-of-range values to a supported default.
   */
  desiredRetention?: number;
}

/**
 * The scheduling a response produces, or null when the response is not one of
 * the four ratings. Dates are ISO strings so the result is Redux-serializable.
 */
export const getNewScheduling = (
  flashcard: SchedulableFlashcard | null | undefined,
  response: number,
  options?: NewSchedulingOptions
): FlashcardScheduling | null => {
  if (!flashcard) return null;

  const now = new Date();
  const initialScheduling = flashcard.scheduling;

  const variantCount =
    options?.variantCount ?? getFlashcardVariants(flashcard).length;

  const newDifficultyIndex = calcNewDifficultyIndex(
    initialScheduling.difficultyIndex,
    variantCount,
    response
  );

  const schedulingOptions = new FSRS({
    requestRetention: options?.desiredRetention,
  }).repeat(initialScheduling, now);

  const rating = RATING_BY_RESPONSE[response];
  if (!rating) {
    console.error('Invalid response value:', response);
    return null;
  }

  const newScheduling = schedulingOptions[rating].card;
  newScheduling.difficultyIndex = newDifficultyIndex;

  // Convert Date objects to ISO strings for Redux serialization. Guard
  // against an invalid Date (NaN time value) so a single corrupt card can
  // never crash the study session at `.toISOString()`; fall back to `now`.
  const serializeDate = (value: Date | string): Date | string =>
    value instanceof Date
      ? Number.isNaN(value.getTime())
        ? now.toISOString()
        : value.toISOString()
      : value;

  newScheduling.due = serializeDate(newScheduling.due);
  newScheduling.lastReview = serializeDate(newScheduling.lastReview);

  return newScheduling;
};

const normalizeNonNegativeInt = (value: number) =>
  Math.max(0, Math.floor(value));

/** The whole-number normalization the respond endpoint expects. */
export const normalizeSchedulingForPersist = (
  scheduling: FlashcardScheduling
): FlashcardScheduling => ({
  ...scheduling,
  elapsedDays: normalizeNonNegativeInt(scheduling.elapsedDays),
  scheduledDays: normalizeNonNegativeInt(scheduling.scheduledDays),
  reps: normalizeNonNegativeInt(scheduling.reps),
  lapses: normalizeNonNegativeInt(scheduling.lapses),
  state: normalizeNonNegativeInt(scheduling.state),
  difficultyIndex: normalizeNonNegativeInt(scheduling.difficultyIndex),
});
